import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import moment from "moment";
import {
  RiMoneyDollarCircleLine,
  RiHandCoinLine,
  RiLockLine,
  RiCheckboxCircleLine,
  RiTimeLine,
  RiFlightTakeoffLine,
  RiBuildingLine,
  RiSearchLine,
} from "react-icons/ri";

const MasterCommissionsList = () => {
  const { user, loading } = useAuth();

  // Agent selection states
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Commission data states
  const [summaryData, setSummaryData] = useState({
    totalCommission: 0,
    readyToWithdraw: 0,
    earnedReleased: 0,
    lockedInEscrow: 0,
    commissionList: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [releasingBookingId, setReleasingBookingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL"); // ALL, READY, LOCKED, RELEASED

  // 1. Fetch All Client Agents for Dropdown
  useEffect(() => {
    const fetchAllAgents = async () => {
      setAgentsLoading(true);
      try {
        const url = `${config.GETCOMPANIES || config.GETALLCOMPANIES}?companyType=CLIENT`;
        const res = await apiService.getAll(url);
        const list = Array.isArray(res) ? res : res?.data || [];
        setAgents(list);

        if (list.length > 0 && !selectedAgentId) {
          setSelectedAgentId(list[0].companyId);
        }
      } catch (err) {
        console.error("Error fetching client agents:", err);
      } finally {
        setAgentsLoading(false);
      }
    };

    if (user && !loading) {
      fetchAllAgents();
    }
  }, [user, loading]);

  // 2. Fetch Selected Agent's Commission Summary
  const fetchAgentCommissions = async (agentId) => {
    const targetId = agentId || selectedAgentId;
    if (!targetId) return;

    setIsLoading(true);
    try {
      const endpoint = `${config.GETAGENTCOMMISSIONSUMMARY}/${targetId}`;
      const res = await apiService.getAll(endpoint);
      if (res) {
        setSummaryData({
          totalCommission: res.totalCommission || 0,
          readyToWithdraw: res.readyToWithdraw || 0,
          earnedReleased: res.earnedReleased || 0,
          lockedInEscrow: res.lockedInEscrow || 0,
          commissionList: res.commissionList || [],
        });
      }
    } catch (err) {
      console.error("Error fetching agent commission summary:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAgentId) {
      fetchAgentCommissions(selectedAgentId);
    }
  }, [selectedAgentId]);

  const handleAgentChange = (e) => {
    const newAgentId = e.target.value;
    setSelectedAgentId(newAgentId);
  };

  // 3. One-Click Admin Action: Release Commission to Agent's Active Wallet
  // In MasterCommissionsList.jsx (handleReleaseCommission alert):
const handleReleaseCommission = async (bookingId, amount) => {
  const selectedAgent = agents.find((a) => a.companyId === selectedAgentId);
  const agentName = selectedAgent?.companyName || selectedAgent?.brandName || "Agent";

  const gross = Number(amount) || 0;
  const estimatedTds = Number((gross * 0.02).toFixed(2)); // 2% TDS preview
  const netCredit = Number((gross - estimatedTds).toFixed(2));

  if (
    !window.confirm(
      `Release Commission for Booking: ${bookingId} to ${agentName}?\n\n` +
      `• Gross Commission: INR ${gross.toLocaleString("en-IN", { minimumFractionDigits: 2 })}\n` +
      `• Less TDS (Sec 194H @ 2%): -INR ${estimatedTds.toLocaleString("en-IN", { minimumFractionDigits: 2 })}\n` +
      `• Net Credited to Wallet: INR ${netCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    )
  ) {
    return;
  }

  setReleasingBookingId(bookingId);
  try {
    const endpoint = config.RELEASEAGENTCOMMISSION;
    const payload = {
      agentCompanyId: selectedAgentId,
      bookingId: bookingId,
    };

    const res = await apiService.create(endpoint, payload);
    if (res && (res.succeeded || res.success || res.status)) {
      alert(res.message || "Commission successfully released to agent's wallet!");
      await fetchAgentCommissions(selectedAgentId);
    } else {
      alert(res?.message || "Failed to release commission.");
    }
  } catch (err) {
    console.error("Error releasing commission:", err);
    alert(err.response?.data?.message || "Error releasing commission.");
  } finally {
    setReleasingBookingId(null);
  }
};

  const formatAmount = (val) =>
    Number(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  // Filter List
  const filteredList = summaryData.commissionList.filter((item) => {
    const matchesSearch =
      item.bookingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.pnr?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.passengerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sector?.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeFilter === "READY") return matchesSearch && item.status === "READY";
    if (activeFilter === "LOCKED") return matchesSearch && item.status === "LOCKED";
    if (activeFilter === "RELEASED") return matchesSearch && item.status === "RELEASED";
    return matchesSearch;
  });

  const selectedAgentObj = agents.find((a) => a.companyId === selectedAgentId);
  const selectedAgentName = selectedAgentObj?.companyName || selectedAgentObj?.brandName || "Agent";

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            
            {/* Header & Agent Selector Dropdown */}
            <div className="card shadow-sm border-0 mb-4 p-3 bg-white">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h4 className="card-title mb-1 fw-bold text-primary">
                    👑 Master Commission & Escrow Management
                  </h4>
                  <p className="text-muted small mb-0">
                    Review and authorize departed flight commissions into agent wallets
                  </p>
                </div>

                {/* Agent Selector Bar */}
                <div className="d-flex align-items-center gap-2 bg-light p-2 rounded border">
                  <RiBuildingLine size={20} className="text-primary" />
                  <label className="small fw-bold mb-0 text-muted">Select Agent:</label>
                  <select
                    className="form-select form-select-sm border-0 fw-bold bg-white"
                    style={{ width: "240px", height: "38px" }}
                    value={selectedAgentId}
                    onChange={handleAgentChange}
                    disabled={agentsLoading}
                  >
                    {agents.map((agent) => (
                      <option key={agent.companyId} value={agent.companyId}>
                        🏢 {agent.companyName || agent.brandName}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary btn-sm fw-bold"
                    onClick={() => fetchAgentCommissions(selectedAgentId)}
                    disabled={isLoading}
                  >
                    <i className="ri-refresh-line"></i> Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* ─── 4 SUMMARY METRIC CARDS ─── */}
            <div className="row mb-4">
              {/* 🟢 1. EARNED (RELEASED TO WALLET) */}
              <div className="col-xl-3 col-md-6">
                <div className="card shadow-sm border-0" style={{ borderLeft: "4px solid #059669" }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted text-uppercase small fw-bold d-block mb-1">
                          Settled in {selectedAgentName}'s Wallet
                        </span>
                        <h3 className="fw-bold text-success mb-0">
                          INR {formatAmount(summaryData.earnedReleased)}
                        </h3>
                        <small className="text-muted">Already transferred to active balance</small>
                      </div>
                      <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3 p-2">
                          <RiCheckboxCircleLine />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 🔵 2. READY TO RELEASE */}
              <div className="col-xl-3 col-md-6">
                <div className="card shadow-sm border-0" style={{ borderLeft: "4px solid #2563eb" }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted text-uppercase small fw-bold d-block mb-1">
                          Ready for Platform Release
                        </span>
                        <h3 className="fw-bold text-primary mb-0">
                          INR {formatAmount(summaryData.readyToWithdraw)}
                        </h3>
                        <small className="text-primary fw-medium">Flight departed & unlocked</small>
                      </div>
                      <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3 p-2">
                          <RiHandCoinLine />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 🔒 3. LOCKED IN ESCROW */}
              <div className="col-xl-3 col-md-6">
                <div className="card shadow-sm border-0" style={{ borderLeft: "4px solid #6b7280" }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted text-uppercase small fw-bold d-block mb-1">
                          Locked in Escrow
                        </span>
                        <h3 className="fw-bold text-secondary mb-0">
                          INR {formatAmount(summaryData.lockedInEscrow)}
                        </h3>
                        <small className="text-muted">Unlocks on departure date</small>
                      </div>
                      <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-light text-secondary rounded-circle fs-3 p-2">
                          <RiLockLine />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 🟡 4. TOTAL COMMISSION GROSS */}
              <div className="col-xl-3 col-md-6">
                <div className="card shadow-sm border-0" style={{ borderLeft: "4px solid #d97706" }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted text-uppercase small fw-bold d-block mb-1">
                          Total Escrow Gross
                        </span>
                        <h3 className="fw-bold text-warning mb-0">
                          INR {formatAmount(summaryData.totalCommission)}
                        </h3>
                        <small className="text-muted">Total earned across all tickets</small>
                      </div>
                      <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-3 p-2">
                          <RiMoneyDollarCircleLine />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── COMMISSIONS TABLE ─── */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white py-3 border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Filter Pills */}
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${activeFilter === "ALL" ? "btn-dark fw-bold" : "btn-outline-secondary"}`}
                    onClick={() => setActiveFilter("ALL")}
                  >
                    All ({summaryData.commissionList.length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === "READY" ? "btn-primary fw-bold" : "btn-outline-secondary"}`}
                    onClick={() => setActiveFilter("READY")}
                  >
                    Ready for Release ({summaryData.commissionList.filter((c) => c.status === "READY").length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === "LOCKED" ? "btn-secondary fw-bold" : "btn-outline-secondary"}`}
                    onClick={() => setActiveFilter("LOCKED")}
                  >
                    Locked Escrow ({summaryData.commissionList.filter((c) => c.status === "LOCKED").length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${activeFilter === "RELEASED" ? "btn-success fw-bold" : "btn-outline-secondary"}`}
                    onClick={() => setActiveFilter("RELEASED")}
                  >
                    Settled in Wallet ({summaryData.commissionList.filter((c) => c.status === "RELEASED").length})
                  </button>
                </div>

                {/* Search Box */}
                <div style={{ maxWidth: "260px" }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search PNR, Booking ID, Name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="card-body p-0">
                {isLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Calculating commission ledger for {selectedAgentName}...</p>
                  </div>
                ) : (
                  <div className="table-responsive table-card">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3" style={{ width: "50px" }}>#</th>
                          <th>Booking Ref / PNR</th>
                          <th>Passenger Name</th>
                          <th>Route / Sector</th>
                          <th>Booking Date</th>
                          <th>Departure Date</th>
                          <th className="text-end">Commission (INR)</th>
                          <th className="text-center">Escrow Status</th>
                          <th className="text-end pe-3">Admin Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredList.length > 0 ? (
                          filteredList.map((item, idx) => {
                            const isReady = item.status === "READY";
                            const isReleased = item.status === "RELEASED";
                            const isLocked = item.status === "LOCKED";
                            const isReleasing = releasingBookingId === item.bookingId;

                            return (
                              <tr key={idx}>
                                <td className="ps-3 text-muted">{idx + 1}</td>
                                <td>
                                  <strong className="text-primary d-block">{item.bookingId}</strong>
                                  <span className="badge bg-light text-dark border">
                                    PNR: {item.pnr}
                                  </span>
                                </td>
                                <td className="fw-medium text-dark">{item.passengerName}</td>
                                <td>
                                  <span className="d-inline-flex align-items-center gap-1">
                                    <RiFlightTakeoffLine className="text-muted" /> {item.sector}
                                  </span>
                                </td>
                                <td className="small text-muted">
                                  {moment(item.bookingDate).format("DD-MMM-YYYY")}
                                </td>
                                <td>
                                  {item.departureDate ? (
                                    <span className="fw-medium">
                                      {moment(item.departureDate).format("DD-MMM-YYYY")}
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td className="text-end fw-bold text-success fs-6">
                                  +₹{formatAmount(item.commissionAmount)}
                                </td>
                                <td className="text-center">
                                  {isReleased && (
                                    <span className="badge bg-success-subtle text-success">
                                      <RiCheckboxCircleLine className="me-1" /> Settled in Wallet
                                    </span>
                                  )}
                                  {isReady && (
                                    <span className="badge bg-primary-subtle text-primary">
                                      <RiHandCoinLine className="me-1" /> Ready for Release
                                    </span>
                                  )}
                                  {isLocked && (
                                    <span className="badge bg-secondary-subtle text-secondary">
                                      <RiLockLine className="me-1" /> Locked in Escrow
                                    </span>
                                  )}
                                </td>
                                <td className="text-end pe-3">
                                  {/* ⚡ ACTIVE ADMIN ACTION */}
                                  {isReady && (
                                    <button
                                      className="btn btn-sm btn-success fw-bold"
                                      disabled={isReleasing}
                                      onClick={() => handleReleaseCommission(item.bookingId, item.commissionAmount)}
                                    >
                                      {isReleasing ? "Releasing..." : "⚡ Release to Agent Wallet"}
                                    </button>
                                  )}

                                  {isLocked && (
                                    <span className="small text-muted" title="Unlocks on departure date">
                                      <RiTimeLine className="me-1" /> Unlocks on departure
                                    </span>
                                  )}

                                  {isReleased && (
                                    <span className="small text-success fw-medium">
                                      ✔ Transferred
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-5 text-muted">
                              <RiMoneyDollarCircleLine size={40} className="mb-2 text-muted" />
                              <p className="mb-0">No commissions found for {selectedAgentName}.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default MasterCommissionsList;